import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface Policy {
  id: string
  name: string
  retention_years: number
  datasets: number
  storage_tb: number
  status: string
  next_action: string
}

interface LifecycleStage {
  stage: string
  datasets: number
  storage_tb: number
  percentage: number
}

interface Statistics {
  active_policies: number
  datasets_covered: number
  data_archived_tb: number
  data_deleted_tb: number
  compliance_rate: number
  storage_saved_pct: number
}

export default function DataRetentionPage() {
  const [policies, setPolicies] = useState<Policy[]>([])
  const [lifecycleStages, setLifecycleStages] = useState<LifecycleStage[]>([])
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const [policiesRes, lifecycleRes, statsRes] = await Promise.all([
        fetch('/api/dataretention/policies'),
        fetch('/api/dataretention/lifecycle'),
        fetch('/api/dataretention/statistics')
      ])
      if (policiesRes.ok) setPolicies(await policiesRes.json())
      if (lifecycleRes.ok) setLifecycleStages(await lifecycleRes.json())
      if (statsRes.ok) setStatistics(await statsRes.json())
    } catch (error) {
      console.error('Error fetching data retention:', error)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Data Retention</h1>
          <p className="mt-2 text-gray-600">Phase 267 - Lifecycle and archival policies</p>
        </div>

        {statistics && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Active Policies</p>
              <p className="text-2xl font-bold text-gray-900">{statistics.active_policies}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Datasets Covered</p>
              <p className="text-2xl font-bold text-blue-600">{statistics.datasets_covered}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Archived</p>
              <p className="text-2xl font-bold text-purple-600">{statistics.data_archived_tb} TB</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Deleted</p>
              <p className="text-2xl font-bold text-red-600">{statistics.data_deleted_tb} TB</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Compliance</p>
              <p className="text-2xl font-bold text-green-600">{statistics.compliance_rate}%</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Storage Saved</p>
              <p className="text-2xl font-bold text-green-600">{statistics.storage_saved_pct}%</p>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow mb-6">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold">Data Lifecycle</h2>
          </div>
          <div className="p-6 grid grid-cols-1 md:grid-cols-4 gap-4">
            {lifecycleStages.map((stage, idx) => (
              <div key={idx} className="p-4 bg-gray-50 rounded-lg">
                <div className="text-center mb-3">
                  <span className="text-lg font-bold text-gray-900">{stage.stage}</span>
                  <p className="text-sm text-gray-500">{stage.percentage}% of data</p>
                </div>
                <div className="h-2 bg-gray-200 rounded-full mb-3">
                  <div className="h-2 bg-blue-500 rounded-full" style={{width: `${stage.percentage}%`}}></div>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="text-center p-2 bg-white rounded">
                    <p className="text-gray-500">Datasets</p>
                    <p className="font-medium text-blue-600">{stage.datasets}</p>
                  </div>
                  <div className="text-center p-2 bg-white rounded">
                    <p className="text-gray-500">Storage</p>
                    <p className="font-medium text-purple-600">{stage.storage_tb} TB</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold">Retention Policies</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Policy</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Retention</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Datasets</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Storage</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Next Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {policies.map((policy) => (
                  <tr key={policy.id}>
                    <td className="px-6 py-4 font-medium text-gray-900">{policy.name}</td>
                    <td className="px-6 py-4 text-blue-600">{policy.retention_years} years</td>
                    <td className="px-6 py-4 text-gray-500">{policy.datasets}</td>
                    <td className="px-6 py-4 text-purple-600">{policy.storage_tb} TB</td>
                    <td className="px-6 py-4">
                      <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">{policy.status}</span>
                    </td>
                    <td className="px-6 py-4 text-gray-500">{policy.next_action}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  )
}
